import { useEffect, useState } from 'react'
import { Badge, BlockStack, Button, Card, Divider, InlineStack, Spinner, Text } from '@shopify/polaris'
import { healthApi, type CategoryReport, type OrderStats } from '../../../../sdk/health.api'
import { getShop } from '../../../../sdk/shop'

type StatRowProps = {
  color:  string
  label:  string
  count:  number
}

function StatRow({ color, label, count }: StatRowProps) {
  return (
    <InlineStack align="space-between" blockAlign="center">
      <InlineStack gap="200" blockAlign="center">
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <Text as="span" variant="bodySm">{label}</Text>
      </InlineStack>
      <Text as="span" variant="bodySm" fontWeight="semibold">{count.toLocaleString()}</Text>
    </InlineStack>
  )
}

type Props = {
  cat?:         CategoryReport
  onViewIssues: (validatorId: string) => void
  refreshKey?:  number
}

export function OrdersHealthCard({ cat, onViewIssues, refreshKey }: Props) {
  const shop = getShop() ?? ''
  const [stats,   setStats]   = useState<OrderStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)

  useEffect(() => {
    if (!shop) return
    setLoading(true)
    setError(null)
    healthApi.getOrderStats(shop)
      .then(setStats)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false))
  }, [shop, refreshKey])

  const invalidStatus = cat?.validators.find(v => v.validatorId === 'orders.invalid-status')
  const hasIssues     = (invalidStatus?.affectedCount ?? 0) > 0

  return (
    <Card>
      <BlockStack gap="300">

        {/* Header */}
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="050">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h3" variant="headingSm">Orders</Text>
              {stats && (
                <Badge tone="new">{`${stats.total.toLocaleString()} total`}</Badge>
              )}
            </InlineStack>
            <Text as="p" variant="bodySm" tone="subdued">
              Order pipeline and data quality
            </Text>
          </BlockStack>
        </InlineStack>

        {/* Stats */}
        {loading && !stats && (
          <InlineStack gap="200" blockAlign="center">
            <Spinner size="small" accessibilityLabel="Loading order stats" />
            <Text as="p" variant="bodySm" tone="subdued">Loading…</Text>
          </InlineStack>
        )}

        {error && <Text as="p" variant="bodySm" tone="critical">{error}</Text>}

        {stats && stats.total === 0 && (
          <Text as="p" variant="bodySm" tone="subdued">No order data yet.</Text>
        )}

        {stats && stats.total > 0 && (
          <BlockStack gap="150">
            <StatRow color="#108043" label="Delivered"   count={stats.fulfilled}  />
            <StatRow color="#0091ae" label="Active"      count={stats.inProgress} />
            <StatRow color="#e05c00" label="Returns"     count={stats.returns}    />
            <StatRow color="#8c9196" label="Cancelled"   count={stats.cancelled}  />
          </BlockStack>
        )}

        {/* Invalid Status issue */}
        {hasIssues && (
          <>
            <Divider />
            <InlineStack align="space-between" blockAlign="center">
              <InlineStack gap="150" blockAlign="center">
                <Badge tone="warning">Warning</Badge>
                <Text as="span" variant="bodySm">
                  {invalidStatus!.affectedCount} orders with invalid status
                </Text>
              </InlineStack>
              <Button
                variant="plain"
                size="slim"
                onClick={() => onViewIssues('orders.invalid-status')}
              >
                View
              </Button>
            </InlineStack>
          </>
        )}

      </BlockStack>
    </Card>
  )
}
